import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Tabs, TabsContent, TabsList, TabsTrigger
} from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Briefcase, Plus, QrCode, Link2, Copy, Download, Trash2,
  Loader2, FileText, Search, CheckCircle, XCircle, Eye, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

import { API_BASE as API } from '../lib/apiBase';
import { fetchWithRetry } from '../lib/fetchWithRetry';
import { safeJson } from '../lib/safeJson';

const headers = () => {
  const t = localStorage.getItem('hrms-token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const jheaders = () => ({ 'Content-Type': 'application/json', ...headers() });

const CATEGORIES = ['Teaching', 'Non-Teaching', 'Technical', 'Admin'];

function DepartmentTagInput({ value, onChange, testId, suggestions = [] }) {
  const [draft, setDraft] = useState('');
  const [focused, setFocused] = useState(false);
  const tags = Array.isArray(value) ? value : [];
  const lower = (s) => (s || '').toLowerCase();
  const matching = suggestions
    .filter((s) => !tags.some((t) => lower(t) === lower(s)))
    .filter((s) => !draft || lower(s).includes(lower(draft)))
    .slice(0, 12);
  const commit = (raw) => {
    const v = (raw ?? draft).trim();
    if (!v) return;
    const parts = v.split(',').map((s) => s.trim()).filter(Boolean);
    // Case-insensitive de-dupe against existing tags
    const existing = new Set(tags.map(lower));
    const next = [...tags];
    parts.forEach((p) => { if (!existing.has(lower(p))) { existing.add(lower(p)); next.push(p); } });
    onChange(next);
    setDraft('');
  };
  const remove = (idx) => onChange(tags.filter((_, i) => i !== idx));
  return (
    <div className="relative">
      <div className="border rounded-md p-2 flex flex-wrap gap-1 items-center min-h-[42px] bg-white dark:bg-slate-900" data-testid={testId}>
        {tags.map((t, i) => (
          <span key={i} className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-100 text-xs px-2 py-0.5 rounded-full">
            {t}
            <button type="button" className="hover:text-red-600" onClick={() => remove(i)} data-testid={`${testId}-remove-${i}`}>×</button>
          </span>
        ))}
        <input
          className="flex-1 min-w-[140px] outline-none text-sm bg-transparent"
          placeholder={tags.length ? 'Add another department...' : 'Pick or type a department'}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 150)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); commit(); }
            else if (e.key === 'Backspace' && !draft && tags.length) { remove(tags.length - 1); }
          }}
          data-testid={`${testId}-field`}
        />
      </div>
      {focused && (matching.length > 0 || draft) && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white dark:bg-slate-900 border rounded-md shadow-lg max-h-60 overflow-y-auto" data-testid={`${testId}-suggestions`}>
          {matching.map((s) => (
            <button
              type="button"
              key={s}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50 dark:hover:bg-slate-800"
              onMouseDown={(e) => { e.preventDefault(); commit(s); }}
              data-testid={`${testId}-suggest-${s.replace(/\s+/g, '_').toLowerCase()}`}
            >
              {s}
            </button>
          ))}
          {draft && !matching.some((s) => lower(s) === lower(draft)) && (
            <button
              type="button"
              className="w-full text-left px-3 py-1.5 text-sm font-medium text-blue-700 dark:text-blue-300 border-t hover:bg-blue-50 dark:hover:bg-slate-800"
              onMouseDown={(e) => { e.preventDefault(); commit(); }}
              data-testid={`${testId}-add-new`}
            >
              + Add new department "{draft.trim()}"
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const STATUS_BADGES = {
  submitted: { label: 'Submitted', cls: 'bg-blue-100 text-blue-700' },
  shortlisted: { label: 'Shortlisted', cls: 'bg-amber-100 text-amber-700' },
  hod_review: { label: 'HOD Review', cls: 'bg-violet-100 text-violet-700' },
  forwarded_to_director: { label: 'Director Review', cls: 'bg-fuchsia-100 text-fuchsia-700' },
  recommended: { label: 'Recommended', cls: 'bg-teal-100 text-teal-700' },
  approved: { label: 'Approved', cls: 'bg-emerald-100 text-emerald-700' },
  hired: { label: 'Hired', cls: 'bg-green-200 text-green-800' },
  rejected: { label: 'Rejected', cls: 'bg-red-100 text-red-700' },
};

export default function Recruitment() {
  const { user } = useAuth();
  const ctx = useOutletContext() || {};
  const selectedEntity = ctx.selectedEntity;

  const [tab, setTab] = useState('jobs');
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [deptSuggestions, setDeptSuggestions] = useState([]);
  const [hodList, setHodList] = useState([]);

  const [jobDialogOpen, setJobDialogOpen] = useState(false);
  const [creatingJob, setCreatingJob] = useState(false);
  const blankForm = {
    title: '', category: 'Teaching', wing: 'Degree', departments: [], designation: '',
    designation_custom: false,
    vacancies: 1, min_experience_years: 0, pay_scale: '', eligibility: '',
    description: '', deadline: '', assigned_hod_employee_id: ''
  };
  const [jobForm, setJobForm] = useState(blankForm);

  // Wing → designation catalog (matches /recruitment/teaching-meta backend)
  const TEACHING_WINGS = {
    'Degree': ['Professor', 'Associate Professor', 'Assistant Professor'],
    'Diploma (Polytechnic)': ['Lecturer'],
  };
  const [qrJob, setQrJob] = useState(null);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeConfirm, setPurgeConfirm] = useState('');
  const [purging, setPurging] = useState(false);
  const [appDetail, setAppDetail] = useState(null);
  const [filters, setFilters] = useState({ job_code: '', department: '', category: '', status: '' });
  const [searchTerm, setSearchTerm] = useState('');
  // Phase 2 queues
  const [hodQueue, setHodQueue] = useState([]);
  const [directorQueue, setDirectorQueue] = useState([]);
  const [hireQueue, setHireQueue] = useState([]);

  const isHRorAdmin = ['hr', 'admin', 'super_admin'].includes(user?.role);
  const isHOD = ['hod', 'super_admin', 'admin'].includes(user?.role);
  const isDirector = ['director', 'principal', 'super_admin'].includes(user?.role);
  const isSuperAdmin = user?.role === 'super_admin';
  const canAccess = isHRorAdmin || isHOD || isDirector;

  const fetchJobs = useCallback(async () => {
    try {
      const res = await fetchWithRetry(`${API}/api/recruitment/jobs`, { headers: headers(), credentials: 'include' });
      if (res.ok) setJobs(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  const fetchApplications = useCallback(async () => {
    try {
      const q = new URLSearchParams();
      if (filters.job_code) q.set('job_code', filters.job_code);
      if (filters.department) q.set('department', filters.department);
      if (filters.category) q.set('category', filters.category);
      if (filters.status) q.set('status', filters.status);
      const res = await fetchWithRetry(`${API}/api/recruitment/applications?${q}`, { headers: headers(), credentials: 'include' });
      if (res.ok) setApplications(await res.json());
    } catch (e) { console.error(e); }
  }, [filters]);

  const fetchEmployees = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/employees`, { headers: headers(), credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setEmployees(Array.isArray(data) ? data : (data.employees || []));
      }
    } catch (e) { console.error(e); }
  }, []);

  const fetchRecruitmentMeta = useCallback(async () => {
    try {
      const [dRes, hRes] = await Promise.all([
        fetch(`${API}/api/recruitment/meta/departments`, { headers: headers(), credentials: 'include' }),
        fetch(`${API}/api/recruitment/meta/hods`, { headers: headers(), credentials: 'include' }),
      ]);
      if (dRes.ok) setDeptSuggestions(((await dRes.json()).departments) || []);
      if (hRes.ok) setHodList(((await hRes.json()).hods) || []);
    } catch (e) { console.error(e); }
  }, []);

  const fetchHodQueue = useCallback(async () => {
    if (!isHOD) return;
    try {
      const res = await fetchWithRetry(`${API}/api/recruitment/queue/hod`, { headers: headers(), credentials: 'include' });
      if (res.ok) setHodQueue(await res.json());
    } catch (e) { console.error(e); }
  }, [isHOD]);

  const fetchDirectorQueue = useCallback(async () => {
    if (!isDirector) return;
    try {
      const res = await fetchWithRetry(`${API}/api/recruitment/queue/director`, { headers: headers(), credentials: 'include' });
      if (res.ok) setDirectorQueue(await res.json());
    } catch (e) { console.error(e); }
  }, [isDirector]);

  const fetchHireQueue = useCallback(async () => {
    if (!isHRorAdmin) return;
    try {
      const res = await fetchWithRetry(`${API}/api/recruitment/queue/hr-approval`, { headers: headers(), credentials: 'include' });
      if (res.ok) setHireQueue(await res.json());
    } catch (e) { console.error(e); }
  }, [isHRorAdmin]);

  const runPurgeAll = async () => {
    if (purgeConfirm.trim() !== 'DELETE') {
      toast.error('Type DELETE (uppercase) to confirm');
      return;
    }
    setPurging(true);
    try {
      const res = await fetch(`${API}/api/recruitment/purge-all`, {
        method: 'DELETE',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ confirm: 'DELETE' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Purge failed');
      toast.success(`Deleted ${data.deleted_jobs} jobs and ${data.deleted_applications} applications`);
      setPurgeOpen(false);
      setPurgeConfirm('');
      await Promise.all([fetchJobs(), fetchApplications(), fetchHodQueue(), fetchDirectorQueue(), fetchHireQueue()]);
    } catch (e) {
      toast.error(e.message || 'Purge failed');
    } finally {
      setPurging(false);
    }
  };

  const refreshAllQueues = useCallback(() => {
    fetchApplications();
    fetchHodQueue();
    fetchDirectorQueue();
    fetchHireQueue();
  }, [fetchApplications, fetchHodQueue, fetchDirectorQueue, fetchHireQueue]);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      await Promise.all([fetchJobs(), fetchApplications(), fetchEmployees(), fetchRecruitmentMeta(), fetchHodQueue(), fetchDirectorQueue(), fetchHireQueue()]);
      setLoading(false);
    };
    run();
  }, [fetchJobs, fetchApplications, fetchEmployees, fetchRecruitmentMeta, fetchHodQueue, fetchDirectorQueue, fetchHireQueue]);

  useEffect(() => { fetchApplications(); }, [filters, fetchApplications]);

  const handleCreateJob = async (e) => {
    e.preventDefault();
    if (!jobForm.title || !jobForm.category) { toast.error('Title and category are required'); return; }
    setCreatingJob(true);
    try {
      const payload = {
        ...jobForm,
        vacancies: Number(jobForm.vacancies) || 1,
        min_experience_years: Number(jobForm.min_experience_years) || 0,
        entity_id: selectedEntity?.entity_id || '',
        public_base_url: window.location.origin
      };
      const res = await fetch(`${API}/api/recruitment/jobs`, {
        method: 'POST', headers: jheaders(), credentials: 'include',
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast.success('Job posting created');
      setJobDialogOpen(false); setJobForm(blankForm); setQrJob(data);
      await fetchJobs();
    } catch (err) { toast.error(err.message); }
    finally { setCreatingJob(false); }
  };

  const handleDeleteJob = async (job_id) => {
    if (!window.confirm('Delete this job posting? This is permanent.')) return;
    try {
      const res = await fetch(`${API}/api/recruitment/jobs/${job_id}`, { method: 'DELETE', headers: headers(), credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Delete failed');
      toast.success('Job deleted'); fetchJobs();
    } catch (err) { toast.error(err.message); }
  };

  const handleToggleJobStatus = async (job) => {
    const newStatus = job.status === 'active' ? 'closed' : 'active';
    try {
      const res = await fetch(`${API}/api/recruitment/jobs/${job.job_id}`, {
        method: 'PUT', headers: jheaders(), credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Update failed');
      toast.success(`Job marked ${newStatus}`); fetchJobs();
    } catch (err) { toast.error(err.message); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success('Copied!'), () => toast.error('Copy failed'));
  };

  const viewBinary = async (path, downloadName) => {
    try {
      const res = await fetch(`${API}${path}`, { headers: headers(), credentials: 'include' });
      if (!res.ok) {
        const t = await res.json().catch(() => ({}));
        throw new Error(t.detail || `Failed (${res.status})`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      // Open in a new tab for inline viewing; also provide a fallback download
      const w = window.open(url, '_blank');
      if (!w) {
        const a = document.createElement('a');
        a.href = url; a.download = downloadName || 'document.pdf';
        document.body.appendChild(a); a.click(); a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(e.message || 'Could not open file');
    }
  };

  const downloadQR = async (job_code, title) => {
    try {
      const res = await fetch(`${API}/api/recruitment/public/jobs/${job_code}/qr`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `QR_${title.replace(/[^a-z0-9]+/gi, '_')}_${job_code}.png`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('QR download failed'); }
  };

  const downloadExcel = async () => {
    try {
      const q = new URLSearchParams();
      if (filters.job_code) q.set('job_code', filters.job_code);
      if (filters.department) q.set('department', filters.department);
      if (filters.category) q.set('category', filters.category);
      const res = await fetch(`${API}/api/recruitment/applications-export?${q}`, { headers: headers(), credentials: 'include' });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `recruitment_applications_${new Date().toISOString().slice(0, 10)}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Excel downloaded');
    } catch (err) { toast.error(err.message); }
  };

  const setApplicationStatus = async (application_id, status, note = '') => {
    try {
      const res = await fetch(`${API}/api/recruitment/applications/${application_id}/status`, {
        method: 'PUT', headers: jheaders(), credentials: 'include',
        body: JSON.stringify({ status, note })
      });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.detail || 'Update failed');
      toast.success(`Status -> ${status}`); refreshAllQueues();
      if (appDetail?.application_id === application_id) setAppDetail(data);
    } catch (err) { toast.error(err.message); }
  };

  // ---- Phase 2 action helpers ----
  const submitHodScore = async (application_id, payload) => {
    try {
      const res = await fetch(`${API}/api/recruitment/applications/${application_id}/hod-score`, {
        method: 'PUT', headers: jheaders(), credentials: 'include',
        body: JSON.stringify(payload)
      });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.detail || 'Score failed');
      toast.success(`Score saved (${data.hod_score?.total}/${data.hod_score?.max_total})`);
      setAppDetail(data); refreshAllQueues();
    } catch (e) { toast.error(e.message); }
  };

  const forwardToDirector = async (application_id, note = '') => {
    try {
      const res = await fetch(`${API}/api/recruitment/applications/${application_id}/forward-to-director`, {
        method: 'PUT', headers: jheaders(), credentials: 'include',
        body: JSON.stringify({ note })
      });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.detail || 'Forward failed');
      toast.success('Forwarded to Director');
      setAppDetail(data); refreshAllQueues();
    } catch (e) { toast.error(e.message); }
  };

  const submitDirectorReview = async (application_id, payload) => {
    try {
      const res = await fetch(`${API}/api/recruitment/applications/${application_id}/director-review`, {
        method: 'PUT', headers: jheaders(), credentials: 'include',
        body: JSON.stringify(payload)
      });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.detail || 'Review failed');
      toast.success(`Director ${payload.recommendation === 'recommend' ? 'recommended' : 'rejected'} the candidate`);
      setAppDetail(data); refreshAllQueues();
    } catch (e) { toast.error(e.message); }
  };

  const submitOffer = async (application_id, payload) => {
    try {
      const res = await fetch(`${API}/api/recruitment/applications/${application_id}/offer`, {
        method: 'PUT', headers: jheaders(), credentials: 'include',
        body: JSON.stringify(payload)
      });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.detail || 'Offer failed');
      toast.success('Offer saved'); setAppDetail(data); refreshAllQueues();
    } catch (e) { toast.error(e.message); }
  };

  const submitHireApprove = async (application_id) => {
    if (!window.confirm('Approve and hire this candidate? This will create an Employee record and a login User account.')) return;
    try {
      const res = await fetch(`${API}/api/recruitment/applications/${application_id}/hire-approve`, {
        method: 'PUT', headers: jheaders(), credentials: 'include', body: JSON.stringify({})
      });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.detail || 'Hire failed');
      toast.success(`Hired! Employee ID: ${data.employee_id} · temp password: ${data.temp_password}`);
      // refetch detail
      const detailRes = await fetch(`${API}/api/recruitment/applications/${application_id}`, { headers: headers(), credentials: 'include' });
      if (detailRes.ok) setAppDetail(await safeJson(detailRes));
      refreshAllQueues();
    } catch (e) { toast.error(e.message); }
  };

  const visibleApps = applications.filter(a => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (a.candidate_name || '').toLowerCase().includes(s)
      || (a.email || '').toLowerCase().includes(s)
      || (a.phone || '').includes(s)
      || (a.job_title || '').toLowerCase().includes(s);
  });

  if (!canAccess) {
    return (
      <div className="p-6">
        <Card><CardContent className="py-10 text-center text-slate-500">
          <Briefcase className="w-10 h-10 mx-auto mb-2 text-slate-300" />
          You don't have access to the Recruitment module.
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="recruitment-page">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Briefcase className="w-6 h-6 text-[#DC2626]" /> Recruitment
          </h1>
          <p className="text-sm text-slate-500">Post jobs, share QR/links, collect biodata, export Excel.</p>
        </div>
        <div className="flex gap-2">
          {isSuperAdmin ? (
            <Button variant="outline" className="text-red-700 border-red-300 hover:bg-red-50"
                    onClick={() => { setPurgeConfirm(''); setPurgeOpen(true); }}
                    data-testid="purge-recruitment-btn">
              <Trash2 className="w-4 h-4 mr-1.5" /> Purge All Recruitment Data
            </Button>
          ) : null}
          <Button className="bg-[#DC2626] hover:bg-[#B91C1C] text-white" onClick={() => setJobDialogOpen(true)} data-testid="create-job-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New Job Posting
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="jobs" data-testid="tab-jobs"><Briefcase className="w-4 h-4 mr-1.5" /> Job Postings ({jobs.length})</TabsTrigger>
          <TabsTrigger value="applications" data-testid="tab-applications"><FileText className="w-4 h-4 mr-1.5" /> Applications ({applications.length})</TabsTrigger>
          {isHOD && (
            <TabsTrigger value="hod" data-testid="tab-hod-queue">
              HOD Queue ({hodQueue.length})
            </TabsTrigger>
          )}
          {isDirector && (
            <TabsTrigger value="director" data-testid="tab-director-queue">
              Director Queue ({directorQueue.length})
            </TabsTrigger>
          )}
          {isHRorAdmin && (
            <TabsTrigger value="hire" data-testid="tab-hire-queue">
              Hire Approval ({hireQueue.length})
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="jobs" className="space-y-3">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#DC2626] w-8 h-8" /></div>
          ) : jobs.length === 0 ? (
            <Card><CardContent className="py-10 text-center text-slate-400">No jobs yet. Click "New Job Posting" to start.</CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {jobs.map(j => (
                <Card key={j.job_id} className="border-slate-200" data-testid={`job-card-${j.job_code}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <CardTitle className="text-base truncate">{j.title}</CardTitle>
                        <p className="text-xs text-slate-500 mt-0.5">{((j.departments && j.departments.length) ? j.departments.join(', ') : (j.department || '—'))} · {j.designation || '—'}</p>
                      </div>
                      <Badge className={j.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}>{j.status}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex flex-wrap gap-1.5 text-[11px]">
                      <Badge variant="outline">{j.category}</Badge>
                      {j.wing && <Badge variant="outline" className="ml-1 text-[10px] bg-purple-50 border-purple-200 text-purple-700">{j.wing}</Badge>}
                      <Badge variant="outline">Code: {j.job_code}</Badge>
                      <Badge variant="outline">{j.vacancies} vac.</Badge>
                      {j.min_experience_years > 0 && <Badge variant="outline">{j.min_experience_years}+ yrs</Badge>}
                    </div>
                    {j.deadline && <p className="text-xs text-slate-500">Deadline: {j.deadline}</p>}
                    <p className="text-xs text-slate-600"><span className="font-semibold">{j.applications_count}</span> application(s)</p>
                    <div className="flex flex-wrap gap-1.5 pt-2 border-t">
                      <Button size="sm" variant="outline" onClick={() => setQrJob(j)} data-testid={`qr-btn-${j.job_code}`}>
                        <QrCode className="w-3.5 h-3.5 mr-1" /> QR / Link
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => { setFilters({ ...filters, job_code: j.job_code }); setTab('applications'); }} data-testid={`view-apps-${j.job_code}`}>
                        <Eye className="w-3.5 h-3.5 mr-1" /> Apps
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleToggleJobStatus(j)} data-testid={`toggle-status-${j.job_code}`}>
                        {j.status === 'active' ? 'Close' : 'Reopen'}
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50 ml-auto" onClick={() => handleDeleteJob(j.job_id)} data-testid={`delete-job-${j.job_code}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="applications" className="space-y-3">
          <Card><CardContent className="p-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-2">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
                <Input placeholder="Search candidate / email / phone" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-8" data-testid="apps-search" />
              </div>
              <Select value={filters.job_code || 'all'} onValueChange={(v) => setFilters({ ...filters, job_code: v === 'all' ? '' : v })}>
                <SelectTrigger data-testid="filter-job"><SelectValue placeholder="All jobs" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All jobs</SelectItem>
                  {jobs.map(j => <SelectItem key={j.job_code} value={j.job_code}>{j.title} ({j.job_code})</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filters.category || 'all'} onValueChange={(v) => setFilters({ ...filters, category: v === 'all' ? '' : v })}>
                <SelectTrigger data-testid="filter-category"><SelectValue placeholder="All categories" /></SelectTrigger>
                <SelectContent><SelectItem value="all">All categories</SelectItem>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={filters.status || 'all'} onValueChange={(v) => setFilters({ ...filters, status: v === 'all' ? '' : v })}>
                <SelectTrigger data-testid="filter-status"><SelectValue placeholder="All status" /></SelectTrigger>
                <SelectContent><SelectItem value="all">All status</SelectItem>{Object.keys(STATUS_BADGES).map(s => <SelectItem key={s} value={s}>{STATUS_BADGES[s].label}</SelectItem>)}</SelectContent>
              </Select>
              <Button variant="outline" onClick={downloadExcel} data-testid="export-excel-btn">
                <Download className="w-4 h-4 mr-1.5" /> Export Excel
              </Button>
            </div>
          </CardContent></Card>

          <Card><CardContent className="p-0 overflow-x-auto">
            {visibleApps.length === 0 ? (
              <div className="py-12 text-center text-slate-400">No applications match the filters.</div>
            ) : (
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Candidate</TableHead><TableHead>Post</TableHead><TableHead>Department</TableHead>
                  <TableHead>Contact</TableHead><TableHead>Submitted</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {visibleApps.map(a => {
                    const sb = STATUS_BADGES[a.status] || { label: a.status, cls: 'bg-slate-100' };
                    return (
                      <TableRow key={a.application_id} data-testid={`app-row-${a.application_id}`}>
                        <TableCell><p className="font-medium">{a.candidate_name}</p><p className="text-[11px] text-slate-500">{a.application_id}</p></TableCell>
                        <TableCell><div>{a.job_title}</div><Badge variant="outline" className="mt-1 text-[10px]">{a.category}</Badge></TableCell>
                        <TableCell>{a.department || '—'}</TableCell>
                        <TableCell><p className="text-sm">{a.email}</p><p className="text-xs text-slate-500">{a.phone}</p></TableCell>
                        <TableCell>{a.created_at?.slice(0, 10)}</TableCell>
                        <TableCell><Badge className={sb.cls}>{sb.label}</Badge></TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="ghost" onClick={() => setAppDetail(a)} data-testid={`view-app-${a.application_id}`}><Eye className="w-4 h-4" /></Button>
                          {a.status === 'submitted' && (
                            <Button size="sm" variant="ghost" className="text-red-600" title="Reject" onClick={() => setApplicationStatus(a.application_id, 'rejected')} data-testid={`reject-${a.application_id}`}><XCircle className="w-4 h-4" /></Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent></Card>
        </TabsContent>
        {/* Phase 2 queues */}
        {isHOD && (
          <TabsContent value="hod" className="space-y-2">
            <QueueList list={hodQueue} setAppDetail={setAppDetail} emptyText="No applications waiting for HOD review." testidPrefix="hod" />
          </TabsContent>
        )}
        {isDirector && (
          <TabsContent value="director" className="space-y-2">
            <QueueList list={directorQueue} setAppDetail={setAppDetail} emptyText="No candidates awaiting Director interview." testidPrefix="director" />
          </TabsContent>
        )}
        {isHRorAdmin && (
          <TabsContent value="hire" className="space-y-2">
            <QueueList list={hireQueue} setAppDetail={setAppDetail} emptyText="No recommended candidates pending offer / approval." testidPrefix="hire" />
          </TabsContent>
        )}
      </Tabs>

      {/* Create Job Dialog */}
      <Dialog open={jobDialogOpen} onOpenChange={setJobDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Job Posting</DialogTitle>
            <DialogDescription>Once created, share the link or QR. Biodata format auto-switches by category.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateJob} className="space-y-3 mt-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label>Title *</Label><Input value={jobForm.title} onChange={(e) => setJobForm({ ...jobForm, title: e.target.value })} placeholder="e.g. Assistant Professor — Computer Science" data-testid="job-title-input" /></div>
              <div>
                <Label>Category *</Label>
                <Select value={jobForm.category} onValueChange={(v) => {
                  // Reset wing + designation when category flips
                  const next = { ...jobForm, category: v };
                  if (v === 'Teaching') {
                    next.wing = jobForm.wing || 'Degree';
                    next.designation = TEACHING_WINGS[next.wing]?.[0] || '';
                    next.designation_custom = false;
                  } else {
                    next.wing = '';
                    next.designation_custom = false;
                  }
                  setJobForm(next);
                }}>
                  <SelectTrigger data-testid="job-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
                <p className="text-[11px] text-slate-500 mt-1">{jobForm.category === 'Teaching' ? 'Uses Teaching biodata format.' : 'Uses Non-Teaching biodata format.'}</p>
              </div>
              {jobForm.category === 'Teaching' && (
                <div>
                  <Label>Wing *</Label>
                  <Select value={jobForm.wing} onValueChange={(v) => setJobForm({
                    ...jobForm, wing: v,
                    designation: TEACHING_WINGS[v]?.[0] || '',
                    designation_custom: false,
                  })}>
                    <SelectTrigger data-testid="job-wing-select"><SelectValue placeholder="Select wing" /></SelectTrigger>
                    <SelectContent>
                      {Object.keys(TEACHING_WINGS).map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-slate-500 mt-1">{jobForm.wing === 'Diploma (Polytechnic)' ? 'Diploma wing — only Lecturer posts.' : 'Degree wing — Professor / Associate / Assistant.'}</p>
                </div>
              )}
              <div className="md:col-span-2">
                <Label>Departments <span className="text-xs text-slate-500">(comma- or Enter-separated; leave blank if institute-wide)</span></Label>
                <DepartmentTagInput
                  value={jobForm.departments || []}
                  onChange={(arr) => setJobForm({ ...jobForm, departments: arr })}
                  suggestions={deptSuggestions}
                  testId="job-departments-input"
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  {jobForm.departments?.length > 1
                    ? `One posting will accept applications across ${jobForm.departments.length} departments — candidates pick one when applying.`
                    : 'Add multiple departments to avoid creating a separate post for each.'}
                </p>
              </div>
              <div className="md:col-span-2">
                <Label className="flex items-center justify-between">
                  Designation
                  {jobForm.category === 'Teaching' && (
                    <span className="text-[11px] font-normal flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={!!jobForm.designation_custom}
                        onChange={(e) => setJobForm({
                          ...jobForm,
                          designation_custom: e.target.checked,
                          designation: e.target.checked ? jobForm.designation : (TEACHING_WINGS[jobForm.wing]?.[0] || ''),
                        })}
                        data-testid="job-designation-custom-toggle"
                      />
                      Custom designation
                    </span>
                  )}
                </Label>
                {jobForm.category === 'Teaching' && !jobForm.designation_custom ? (
                  <Select value={jobForm.designation} onValueChange={(v) => setJobForm({ ...jobForm, designation: v })}>
                    <SelectTrigger data-testid="job-designation-select"><SelectValue placeholder="Select designation" /></SelectTrigger>
                    <SelectContent>
                      {(TEACHING_WINGS[jobForm.wing] || []).map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input value={jobForm.designation} onChange={(e) => setJobForm({ ...jobForm, designation: e.target.value })} placeholder="Sr. Assistant Professor / Adjunct Faculty / Visiting Lecturer / Clerk" data-testid="job-designation-input" />
                )}
              </div>
              <div><Label>Vacancies</Label><Input type="number" min="1" value={jobForm.vacancies} onChange={(e) => setJobForm({ ...jobForm, vacancies: e.target.value })} data-testid="job-vacancies-input" /></div>
              <div><Label>Min. Experience (yrs)</Label><Input type="number" min="0" step="0.5" value={jobForm.min_experience_years} onChange={(e) => setJobForm({ ...jobForm, min_experience_years: e.target.value })} data-testid="job-exp-input" /></div>
              <div><Label>Pay Scale</Label><Input value={jobForm.pay_scale} onChange={(e) => setJobForm({ ...jobForm, pay_scale: e.target.value })} placeholder="As per AICTE/DBATU norms" data-testid="job-pay-input" /></div>
              <div><Label>Application Deadline</Label><Input type="date" value={jobForm.deadline} onChange={(e) => setJobForm({ ...jobForm, deadline: e.target.value })} data-testid="job-deadline-input" /></div>
              <div className="md:col-span-2">
                <Label>Assign HOD <span className="text-xs text-slate-500">(routes applications directly to this HOD, overrides department string)</span></Label>
                <Select value={jobForm.assigned_hod_employee_id || 'none'} onValueChange={(v) => setJobForm({ ...jobForm, assigned_hod_employee_id: v === 'none' ? '' : v })}>
                  <SelectTrigger data-testid="job-hod-select"><SelectValue placeholder="Select HOD (optional)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None (auto-route by department) —</SelectItem>
                    {hodList.length === 0 ? (
                      <SelectItem value="__empty" disabled>No HOD users found</SelectItem>
                    ) : hodList.filter(h => h.employee_id).map(h => (
                      <SelectItem key={h.employee_id} value={h.employee_id}>
                        {h.name} · {h.department || 'No dept'} · {h.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {jobForm.assigned_hod_employee_id ? (
                  <p className="text-[11px] text-emerald-700 mt-1">
                    ✓ This HOD will see every application to this post in their queue, no matter what the candidate selects.
                  </p>
                ) : null}
              </div>
              <div className="md:col-span-2"><Label>Eligibility (norms)</Label><Textarea rows={2} value={jobForm.eligibility} onChange={(e) => setJobForm({ ...jobForm, eligibility: e.target.value })} placeholder="M.Tech / Ph.D. in CS, NET/SET preferred, as per AICTE/DTE/DBATU/MSBTE norms" data-testid="job-eligibility-input" /></div>
              <div className="md:col-span-2"><Label>Description / Notes</Label><Textarea rows={3} value={jobForm.description} onChange={(e) => setJobForm({ ...jobForm, description: e.target.value })} placeholder="Optional public-facing details" data-testid="job-description-input" /></div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setJobDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#DC2626] hover:bg-[#B91C1C] text-white" disabled={creatingJob} data-testid="submit-job-btn">
                {creatingJob ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Plus className="w-4 h-4 mr-1.5" />} Create & Generate Link
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* QR / Link Dialog */}
      <Dialog open={!!qrJob} onOpenChange={(o) => !o && setQrJob(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Share Application Link</DialogTitle>
            <DialogDescription>{qrJob?.title} · code <span className="font-mono">{qrJob?.job_code}</span></DialogDescription>
          </DialogHeader>
          {qrJob && (
            <div className="space-y-3 mt-2">
              <div className="flex justify-center bg-slate-50 p-4 rounded-lg">
                <img src={`${API}/api/recruitment/public/jobs/${qrJob.job_code}/qr`} alt={`QR for ${qrJob.title}`} className="w-56 h-56 object-contain" data-testid="qr-image" />
              </div>
              <div className="space-y-1.5">
                <Label>Public Application URL</Label>
                <div className="flex gap-1.5">
                  <Input readOnly value={qrJob.apply_url} className="font-mono text-xs" data-testid="apply-url" />
                  <Button size="sm" variant="outline" onClick={() => copyToClipboard(qrJob.apply_url)} data-testid="copy-url-btn"><Copy className="w-4 h-4" /></Button>
                  <Button size="sm" variant="outline" onClick={() => window.open(qrJob.apply_url, '_blank')} data-testid="open-url-btn"><ExternalLink className="w-4 h-4" /></Button>
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => downloadQR(qrJob.job_code, qrJob.title)} className="flex-1" variant="outline" data-testid="download-qr-btn"><Download className="w-4 h-4 mr-1.5" /> Download QR PNG</Button>
                <Button onClick={() => copyToClipboard(qrJob.apply_url)} className="flex-1 bg-[#DC2626] hover:bg-[#B91C1C] text-white"><Link2 className="w-4 h-4 mr-1.5" /> Copy Link</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Purge All Recruitment dialog (Super Admin only) */}
      <Dialog open={purgeOpen} onOpenChange={(o) => { if (!purging) { setPurgeOpen(o); if (!o) setPurgeConfirm(''); } }}>
        <DialogContent className="max-w-md" data-testid="purge-recruitment-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center gap-2">
              <Trash2 className="w-5 h-5" /> Purge ALL Recruitment Data?
            </DialogTitle>
            <DialogDescription>
              This will permanently delete <strong>every job posting and every application</strong>, including all resumes, HOD scores, Director reviews, and recommendation PDFs. The Recruitment module will remain available — you can post new jobs afterwards.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-900">
              <p><strong>Currently in the system:</strong></p>
              <ul className="list-disc pl-5 mt-1">
                <li>{jobs.length} job posting{jobs.length === 1 ? '' : 's'}</li>
                <li>{applications.length} application{applications.length === 1 ? '' : 's'}</li>
              </ul>
              <p className="mt-2 font-semibold">This action cannot be undone.</p>
            </div>
            <div>
              <Label className="text-xs">Type <span className="font-mono font-bold">DELETE</span> to confirm</Label>
              <Input
                value={purgeConfirm}
                onChange={(e) => setPurgeConfirm(e.target.value)}
                placeholder="DELETE"
                data-testid="purge-confirm-input"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setPurgeOpen(false); setPurgeConfirm(''); }} disabled={purging}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={runPurgeAll}
              disabled={purging || purgeConfirm.trim() !== 'DELETE'}
              data-testid="purge-confirm-btn"
            >
              {purging ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1.5" />}
              Purge Everything
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Application Detail Dialog */}
      <Dialog open={!!appDetail} onOpenChange={(o) => !o && setAppDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{appDetail?.candidate_name}</DialogTitle>
            <DialogDescription>{appDetail?.job_title} · {appDetail?.category} · {appDetail?.application_id}</DialogDescription>
          </DialogHeader>
          {appDetail && (
            <div className="space-y-3 mt-2 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><span className="text-slate-500">Email:</span> {appDetail.email}</div>
                <div><span className="text-slate-500">Phone:</span> {appDetail.phone}</div>
                <div><span className="text-slate-500">Department:</span> {appDetail.department || '—'}</div>
                <div><span className="text-slate-500">Designation:</span> {appDetail.designation || '—'}</div>
                <div><span className="text-slate-500">Status:</span> <Badge className={STATUS_BADGES[appDetail.status]?.cls}>{STATUS_BADGES[appDetail.status]?.label || appDetail.status}</Badge></div>
                <div><span className="text-slate-500">Submitted:</span> {appDetail.created_at?.slice(0, 10)}</div>
              </div>

              {/* Resume + Recommendation toolbar */}
              <div className="flex flex-wrap gap-2 border-y py-2">
                <Button size="sm" variant="outline" disabled={!appDetail.resume_base64}
                        onClick={() => viewBinary(`/api/recruitment/applications/${appDetail.application_id}/resume`, `resume_${appDetail.candidate_name || appDetail.application_id}.pdf`)}
                        data-testid="view-resume-btn">
                  <FileText className="w-4 h-4 mr-1.5" />
                  {appDetail.resume_base64 ? 'View Resume' : 'No Resume Uploaded'}
                </Button>
                {appDetail.director_review ? (
                  <Button size="sm" variant="outline" className="text-violet-700 border-violet-300"
                          onClick={() => viewBinary(`/api/recruitment/applications/${appDetail.application_id}/recommendation`, `recommendation_${appDetail.candidate_name || appDetail.application_id}.pdf`)}
                          data-testid="view-recommendation-btn">
                    <Download className="w-4 h-4 mr-1.5" /> Recommendation Sheet (PDF)
                  </Button>
                ) : null}
              </div>

              {appDetail.biodata && (<div className="border rounded p-3 space-y-2 bg-slate-50"><p className="font-semibold text-slate-700">Biodata</p><BiodataView bio={appDetail.biodata} schema={appDetail.schema} /></div>)}

              {/* Phase 2 panels */}
              <HodScoringPanel app={appDetail} canScore={isHOD} onSubmit={(payload) => submitHodScore(appDetail.application_id, payload)} onForward={(note) => forwardToDirector(appDetail.application_id, note)} />
              <DirectorReviewPanel app={appDetail} canReview={isDirector} onSubmit={(payload) => submitDirectorReview(appDetail.application_id, payload)} />
              <OfferPanel app={appDetail} canSet={isHRorAdmin} onSubmit={(payload) => submitOffer(appDetail.application_id, payload)} />
              <HirePanel app={appDetail} canApprove={isSuperAdmin} onApprove={() => submitHireApprove(appDetail.application_id)} />

              <div className="flex flex-wrap gap-2 pt-3 border-t">
                {!['rejected', 'hired'].includes(appDetail.status) && (<Button size="sm" variant="outline" className="text-red-600 border-red-300" onClick={() => setApplicationStatus(appDetail.application_id, 'rejected')}>Reject</Button>)}
              </div>
              {appDetail.stage_history?.length > 0 && (
                <div className="border-t pt-2">
                  <p className="font-semibold text-slate-700 mb-1">Stage History</p>
                  <ul className="text-xs space-y-1">
                    {appDetail.stage_history.map((s, i) => (
                      <li key={i}><span className="font-mono">{s.at?.slice(0, 16).replace('T', ' ')}</span> — <Badge variant="outline" className="text-[10px]">{s.stage}</Badge> {s.by_name && <span>by {s.by_name}</span>} {s.note && <span>· {s.note}</span>}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BiodataView({ bio, schema }) {
  if (!bio) return null;
  const fields = [
    ['Address', bio.address], ['Religion', bio.religion], ['Cast', bio.cast], ['Category', bio.caste_category],
    ['Post Applied', bio.post_applied], ['Department', bio.department],
    ['Teaching Exp (yrs)', bio.teaching_experience_years], ['Industrial Exp (yrs)', bio.industrial_experience_years],
    ['Total Experience', bio.total_experience],
    ['Current Salary', bio.current_salary], ['Expected Salary', bio.expected_salary],
    ['Date', bio.application_date], ['Place', bio.application_place],
  ];
  return (
    <div className="grid grid-cols-2 gap-y-1.5 gap-x-3">
      {fields.filter(([, v]) => v !== undefined && v !== '').map(([k, v]) => (<div key={k}><span className="text-slate-500">{k}:</span> {String(v)}</div>))}
      {schema === 'teaching' && (<>
        {bio.area_of_specialization && <div className="col-span-2"><span className="text-slate-500">Specialization:</span> {bio.area_of_specialization}</div>}
        {bio.subjects_diploma && <div className="col-span-2"><span className="text-slate-500">Subjects (Diploma):</span> {bio.subjects_diploma}</div>}
        {bio.subjects_graduate && <div className="col-span-2"><span className="text-slate-500">Subjects (Graduate):</span> {bio.subjects_graduate}</div>}
        {bio.phd_status && <div><span className="text-slate-500">Ph.D.:</span> {bio.phd_status}</div>}
        {bio.books_published !== undefined && <div><span className="text-slate-500">Books:</span> {bio.books_published}</div>}
        {bio.intl_journal_papers !== undefined && <div><span className="text-slate-500">Intl Papers:</span> {bio.intl_journal_papers}</div>}
        {bio.natl_journal_papers !== undefined && <div><span className="text-slate-500">Natl Papers:</span> {bio.natl_journal_papers}</div>}
        {bio.conference_papers !== undefined && <div><span className="text-slate-500">Conf Papers:</span> {bio.conference_papers}</div>}
        {bio.projects && <div className="col-span-2"><span className="text-slate-500">Projects:</span> {bio.projects}</div>}
        {bio.patents && <div className="col-span-2"><span className="text-slate-500">Patents:</span> {bio.patents}</div>}
      </>)}
      {Array.isArray(bio.qualifications) && bio.qualifications.length > 0 && (
        <div className="col-span-2 mt-2">
          <p className="text-slate-500 mb-1">Qualifications</p>
          <div className="overflow-x-auto"><table className="w-full text-xs border">
            <thead className="bg-slate-100"><tr><th className="border p-1">Degree</th><th className="border p-1">Board</th><th className="border p-1">Year</th><th className="border p-1">%</th><th className="border p-1">College</th><th className="border p-1">Class</th></tr></thead>
            <tbody>{bio.qualifications.map((q, i) => (<tr key={i}><td className="border p-1">{q.degree_branch}</td><td className="border p-1">{q.university_board}</td><td className="border p-1">{q.year_of_passing}</td><td className="border p-1">{q.marks_percent}</td><td className="border p-1">{q.college_name}</td><td className="border p-1">{q.class}</td></tr>))}</tbody>
          </table></div>
        </div>
      )}
      {Array.isArray(bio.employment_history) && bio.employment_history.length > 0 && (
        <div className="col-span-2 mt-2">
          <p className="text-slate-500 mb-1">Employment History</p>
          <div className="overflow-x-auto"><table className="w-full text-xs border">
            <thead className="bg-slate-100"><tr><th className="border p-1">Employer</th><th className="border p-1">Designation</th><th className="border p-1">From</th><th className="border p-1">To</th><th className="border p-1">Salary</th><th className="border p-1">Reason</th></tr></thead>
            <tbody>{bio.employment_history.map((e, i) => (<tr key={i}><td className="border p-1">{e.employer}</td><td className="border p-1">{e.designation}</td><td className="border p-1">{e.from_date}</td><td className="border p-1">{e.to_date}</td><td className="border p-1">{e.gross_salary}</td><td className="border p-1">{e.reason_for_leaving}</td></tr>))}</tbody>
          </table></div>
        </div>
      )}
      {schema !== 'teaching' && bio.remarks && (<div className="col-span-2 mt-2"><span className="text-slate-500">Remarks:</span> {bio.remarks}</div>)}
    </div>
  );
}

// ============ Phase 2 helper components ============

function QueueList({ list, setAppDetail, emptyText, testidPrefix }) {
  if (!list || list.length === 0) {
    return <Card><CardContent className="py-12 text-center text-slate-400">{emptyText}</CardContent></Card>;
  }
  return (
    <Card><CardContent className="p-0 overflow-x-auto">
      <Table>
        <TableHeader><TableRow>
          <TableHead>Candidate</TableHead><TableHead>Post</TableHead><TableHead>Department</TableHead>
          <TableHead>Status</TableHead><TableHead>Submitted</TableHead><TableHead className="text-right">Open</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {list.map(a => {
            const sb = STATUS_BADGES[a.status] || { label: a.status, cls: 'bg-slate-100' };
            return (
              <TableRow key={a.application_id} data-testid={`${testidPrefix}-row-${a.application_id}`}>
                <TableCell><p className="font-medium">{a.candidate_name}</p><p className="text-[11px] text-slate-500">{a.email}</p></TableCell>
                <TableCell>{a.job_title} <Badge variant="outline" className="ml-1 text-[10px]">{a.category}</Badge></TableCell>
                <TableCell>{a.department || '—'}</TableCell>
                <TableCell><Badge className={sb.cls}>{sb.label}</Badge></TableCell>
                <TableCell>{a.created_at?.slice(0, 10)}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => setAppDetail(a)} data-testid={`${testidPrefix}-open-${a.application_id}`}>
                    <Eye className="w-4 h-4" />
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </CardContent></Card>
  );
}

function HodScoringPanel({ app, canScore, onSubmit, onForward }) {
  const isTeaching = app.schema === 'teaching';
  const initial = app.hod_score || {};
  const [form, setForm] = useState({
    subject_knowledge: initial.subject_knowledge ?? '',
    communication: initial.communication ?? '',
    board_work: initial.board_work ?? '',
    handling_queries: initial.handling_queries ?? '',
    body_language: initial.body_language ?? '',
    presentation: initial.presentation ?? '',
    personality: initial.personality ?? '',
    knowledge: initial.knowledge ?? '',
    suitability: initial.suitability ?? '',
    demo_topic: initial.demo_topic ?? '',
    demo_date: initial.demo_date ?? '',
    remark: initial.remark ?? '',
  });
  useEffect(() => {
    setForm({
      subject_knowledge: initial.subject_knowledge ?? '',
      communication: initial.communication ?? '',
      board_work: initial.board_work ?? '',
      handling_queries: initial.handling_queries ?? '',
      body_language: initial.body_language ?? '',
      presentation: initial.presentation ?? '',
      personality: initial.personality ?? '',
      knowledge: initial.knowledge ?? '',
      suitability: initial.suitability ?? '',
      demo_topic: initial.demo_topic ?? '',
      demo_date: initial.demo_date ?? '',
      remark: initial.remark ?? '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app.application_id]);

  if (!['submitted', 'shortlisted', 'hod_review', 'forwarded_to_director', 'recommended', 'approved', 'hired'].includes(app.status)) {
    return null;
  }

  const Fld = ({ label, k, max }) => (
    <div className="space-y-1">
      <Label className="text-xs">{label} <span className="text-slate-400">(/{max})</span></Label>
      <Input type="number" min="0" max={max} step="0.5" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`hod-${k}`} />
    </div>
  );

  return (
    <div className="border rounded p-3 space-y-2 bg-violet-50" data-testid="hod-scoring-panel">
      <div className="flex items-center justify-between">
        <p className="font-semibold text-violet-800">HOD — Demo & Interview Scoring</p>
        {initial.total !== undefined && <Badge className="bg-violet-200 text-violet-900">{initial.total} / {initial.max_total}</Badge>}
      </div>
      {canScore ? (
        <div className="space-y-2">
          {isTeaching ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              <Fld label="Subject Knowledge" k="subject_knowledge" max={10} />
              <Fld label="Communication" k="communication" max={10} />
              <Fld label="Board Work" k="board_work" max={10} />
              <Fld label="Handling Queries" k="handling_queries" max={10} />
              <Fld label="Body Language" k="body_language" max={10} />
              <Fld label="Overall Presentation" k="presentation" max={10} />
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <Fld label="Personality" k="personality" max={5} />
              <Fld label="Communication" k="communication" max={5} />
              <Fld label="Knowledge" k="knowledge" max={5} />
              <Fld label="Body Language" k="body_language" max={5} />
              <div className="space-y-1 col-span-2 md:col-span-4">
                <Label className="text-xs">Suitability</Label>
                <Select value={form.suitability || 'unset'} onValueChange={(v) => setForm({ ...form, suitability: v === 'unset' ? '' : v })}>
                  <SelectTrigger data-testid="hod-suitability"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unset">—</SelectItem>
                    <SelectItem value="Suitable">Suitable</SelectItem>
                    <SelectItem value="Not Suitable">Not Suitable</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div className="space-y-1"><Label className="text-xs">Demo Topic</Label><Input value={form.demo_topic} onChange={(e) => setForm({ ...form, demo_topic: e.target.value })} data-testid="hod-demo-topic" /></div>
            <div className="space-y-1"><Label className="text-xs">Demo Date</Label><Input type="date" value={form.demo_date} onChange={(e) => setForm({ ...form, demo_date: e.target.value })} data-testid="hod-demo-date" /></div>
          </div>
          <div className="space-y-1"><Label className="text-xs">Remark</Label><Textarea rows={2} value={form.remark} onChange={(e) => setForm({ ...form, remark: e.target.value })} data-testid="hod-remark" /></div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" className="bg-violet-600 hover:bg-violet-700 text-white" onClick={() => onSubmit(form)} data-testid="hod-save-score-btn">
              <CheckCircle className="w-4 h-4 mr-1.5" /> Save Score
            </Button>
            {(app.hod_score && ['submitted', 'shortlisted', 'hod_review'].includes(app.status)) && (
              <Button size="sm" className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white" onClick={() => onForward(form.remark)} data-testid="hod-forward-btn">
                Forward to Director
              </Button>
            )}
          </div>
        </div>
      ) : (
        initial.total !== undefined ? (
          <div className="text-xs text-slate-600">Scored {initial.total}/{initial.max_total} by {initial.by_name || 'HOD'}. Remark: {initial.remark || '—'}</div>
        ) : <p className="text-xs text-slate-500">No HOD score yet.</p>
      )}
    </div>
  );
}

function DirectorReviewPanel({ app, canReview, onSubmit }) {
  const initial = app.director_review || {};
  const [form, setForm] = useState({ recommendation: initial.recommendation || '', remark: initial.remark || '', score: initial.score ?? '' });
  useEffect(() => {
    setForm({ recommendation: initial.recommendation || '', remark: initial.remark || '', score: initial.score ?? '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app.application_id]);
  if (!['forwarded_to_director', 'recommended', 'approved', 'hired', 'rejected'].includes(app.status)) return null;
  if (!app.hod_score) return null;

  return (
    <div className="border rounded p-3 space-y-2 bg-fuchsia-50" data-testid="director-panel">
      <div className="flex items-center justify-between">
        <p className="font-semibold text-fuchsia-800">Director (Trustee) — Final Interview</p>
        {initial.recommendation && <Badge className="bg-fuchsia-200 text-fuchsia-900">{initial.recommendation}</Badge>}
      </div>
      {canReview && !['hired', 'rejected'].includes(app.status) ? (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Recommendation</Label>
              <Select value={form.recommendation || 'unset'} onValueChange={(v) => setForm({ ...form, recommendation: v === 'unset' ? '' : v })}>
                <SelectTrigger data-testid="director-recommendation"><SelectValue placeholder="Choose" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unset">—</SelectItem>
                  <SelectItem value="recommend">Recommend for Hire</SelectItem>
                  <SelectItem value="reject">Reject</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label className="text-xs">Overall Score (optional)</Label><Input type="number" min="0" max="100" value={form.score} onChange={(e) => setForm({ ...form, score: e.target.value })} data-testid="director-score" /></div>
          </div>
          <div className="space-y-1"><Label className="text-xs">Remark</Label><Textarea rows={2} value={form.remark} onChange={(e) => setForm({ ...form, remark: e.target.value })} data-testid="director-remark" /></div>
          <Button size="sm" className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white" disabled={!form.recommendation} onClick={() => onSubmit(form)} data-testid="director-submit-btn">
            Submit Final Review
          </Button>
        </div>
      ) : (
        initial.recommendation ? (
          <div className="text-xs text-slate-600">Reviewed by {initial.by_name || 'Director'} — <span className="font-semibold">{initial.recommendation}</span>. {initial.remark}</div>
        ) : <p className="text-xs text-slate-500">Awaiting Director review.</p>
      )}
    </div>
  );
}

function OfferPanel({ app, canSet, onSubmit }) {
  const initial = app.offer || {};
  const [form, setForm] = useState({
    salary_type: initial.salary_type || 'breakdown',
    gross_salary: initial.gross_salary ?? '',
    basic_salary: initial.basic_salary ?? '',
    consolidated_salary: initial.consolidated_salary ?? '',
    joining_date: initial.joining_date || '',
    appointment_type: initial.appointment_type || 'Permanent',
    department: initial.department || app.department || '',
    designation: initial.designation || app.designation || ''
  });
  useEffect(() => {
    setForm({
      salary_type: initial.salary_type || 'breakdown',
      gross_salary: initial.gross_salary ?? '',
      basic_salary: initial.basic_salary ?? '',
      consolidated_salary: initial.consolidated_salary ?? '',
      joining_date: initial.joining_date || '',
      appointment_type: initial.appointment_type || 'Permanent',
      department: initial.department || app.department || '',
      designation: initial.designation || app.designation || ''
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app.application_id]);

  if (!['recommended', 'approved', 'hired'].includes(app.status)) return null;

  return (
    <div className="border rounded p-3 space-y-2 bg-teal-50" data-testid="offer-panel">
      <div className="flex items-center justify-between">
        <p className="font-semibold text-teal-800">HR — Offer Details</p>
        {initial.joining_date && <Badge className="bg-teal-200 text-teal-900">Offer set</Badge>}
      </div>
      {canSet && app.status !== 'hired' ? (
        <div className="space-y-2">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Salary Type</Label>
              <Select value={form.salary_type} onValueChange={(v) => setForm({ ...form, salary_type: v })}>
                <SelectTrigger data-testid="offer-salary-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="breakdown">Breakdown</SelectItem>
                  <SelectItem value="consolidated">Consolidated</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.salary_type === 'consolidated' ? (
              <div className="space-y-1"><Label className="text-xs">Consolidated Salary</Label><Input type="number" min="0" value={form.consolidated_salary} onChange={(e) => setForm({ ...form, consolidated_salary: e.target.value })} data-testid="offer-consolidated" /></div>
            ) : (<>
              <div className="space-y-1"><Label className="text-xs">Gross Salary</Label><Input type="number" min="0" value={form.gross_salary} onChange={(e) => setForm({ ...form, gross_salary: e.target.value })} data-testid="offer-gross" /></div>
              <div className="space-y-1"><Label className="text-xs">Basic Salary</Label><Input type="number" min="0" value={form.basic_salary} onChange={(e) => setForm({ ...form, basic_salary: e.target.value })} data-testid="offer-basic" /></div>
            </>)}
            <div className="space-y-1"><Label className="text-xs">Joining Date *</Label><Input type="date" value={form.joining_date} onChange={(e) => setForm({ ...form, joining_date: e.target.value })} data-testid="offer-joining" /></div>
            <div className="space-y-1">
              <Label className="text-xs">Appointment Type</Label>
              <Select value={form.appointment_type} onValueChange={(v) => setForm({ ...form, appointment_type: v })}>
                <SelectTrigger data-testid="offer-appt-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Permanent">Permanent</SelectItem>
                  <SelectItem value="Contract">Contract</SelectItem>
                  <SelectItem value="Adhoc">Adhoc</SelectItem>
                  <SelectItem value="Guest">Guest</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label className="text-xs">Department</Label><Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} data-testid="offer-dept" /></div>
            <div className="space-y-1"><Label className="text-xs">Designation</Label><Input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} data-testid="offer-desig" /></div>
          </div>
          <Button size="sm" className="bg-teal-600 hover:bg-teal-700 text-white" disabled={!form.joining_date} onClick={() => onSubmit(form)} data-testid="offer-save-btn">
            Save Offer
          </Button>
        </div>
      ) : (
        initial.joining_date ? (
          <div className="text-xs text-slate-600">
            ₹{initial.consolidated_salary || initial.gross_salary}/mo · joining {initial.joining_date} · {initial.appointment_type} · set by {initial.set_by_name}
          </div>
        ) : <p className="text-xs text-slate-500">HR has not set offer details yet.</p>
      )}
    </div>
  );
}

function HirePanel({ app, canApprove, onApprove }) {
  if (!['recommended', 'approved', 'hired'].includes(app.status)) return null;
  return (
    <div className="border rounded p-3 space-y-2 bg-emerald-50" data-testid="hire-panel">
      <div className="flex items-center justify-between">
        <p className="font-semibold text-emerald-800">Super Admin — Final Approval & Hire</p>
        {app.status === 'hired' && <Badge className="bg-emerald-200 text-emerald-900">Hired</Badge>}
      </div>
      {app.status === 'hired' && app.hire_result && (
        <div className="text-xs text-slate-700 space-y-1">
          <p><span className="text-slate-500">Employee ID:</span> <span className="font-mono">{app.hire_result.employee_id}</span></p>
          <p><span className="text-slate-500">Login Email:</span> {app.email}</p>
          {app.hire_result.temp_password && (
            <p><span className="text-slate-500">Temp Password:</span> <span className="font-mono bg-yellow-100 px-1 rounded">{app.hire_result.temp_password}</span> (candidate must change on first login)</p>
          )}
        </div>
      )}
      {canApprove && app.status !== 'hired' && (
        <div>
          {!app.offer?.joining_date ? (
            <p className="text-xs text-amber-700">⚠️ HR must set the offer details (joining date / salary) first.</p>
          ) : (
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={onApprove} data-testid="hire-approve-btn">
              Approve & Hire — Create Employee Account
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

